import { ContentBatchGeneration } from "../../common/contentTypes";

/**
 * Mirrors STALE_GENERATION_MS in the platform's `@polly/modules`
 * (shared/modules/src/content/topics.ts). The admin client cannot import that
 * package, so the value is duplicated — keep the two in sync.
 */
export const STALE_GENERATION_MS = 10 * 60_000;

/**
 * What the run actually is, as opposed to what the row says.
 *
 * The job queue has no retry: if polly-manager dies mid-run the batch keeps
 * `status: "running"` with a frozen heartbeat and nothing ever resolves it. A
 * screen that trusted `status` alone would show a spinner forever. `stalled`
 * is that case, and it renders exactly like `failed` — with Retry.
 */
export type EffectiveGenerationStatus =
  | "running"
  | "failed"
  | "done"
  | "stalled";

export function effectiveGenerationStatus(
  generation: ContentBatchGeneration | null | undefined,
): EffectiveGenerationStatus {
  // An imported batch has no generation block; it is finished by definition.
  if (!generation) {
    return "done";
  }

  if (generation.status === "done" || generation.status === "failed") {
    return generation.status;
  }

  const heartbeat = Date.parse(generation.updatedAt);
  if (Number.isNaN(heartbeat)) {
    // Better to offer a Retry that the platform may refuse than to render a
    // run as healthy forever because its timestamp was unreadable.
    return "stalled";
  }

  // Strictly greater, matching the platform's `updatedAt < cutoff` retry guard:
  // at the exact boundary the server still refuses, so the button would lie.
  return Date.now() - heartbeat > STALE_GENERATION_MS ? "stalled" : "running";
}

/** True when the admin can act — `contentGenerateRetry` accepts only these. */
export function isRetryable(status: EffectiveGenerationStatus): boolean {
  return status === "failed" || status === "stalled";
}

export function generationProgress(
  generation: ContentBatchGeneration | null | undefined,
): { produced: number; requested: number; percent: number } {
  const produced = generation?.produced ?? 0;
  const requested = generation?.requested ?? 0;

  return {
    produced,
    requested,
    // A chunk that raced a terminal transition is recorded unguarded, so
    // produced can legitimately overshoot; a bar past 100% just looks broken.
    percent:
      requested > 0
        ? Math.min(100, Math.round((produced / requested) * 100))
        : 0,
  };
}

/** Copy for the danger badge — the platform's reason when it has one. */
export function generationFailureText(
  generation: ContentBatchGeneration | null | undefined,
  status: EffectiveGenerationStatus,
): string | null {
  if (status === "stalled") {
    return "Stalled — no heartbeat for 10+ minutes. Retry to resume.";
  }
  if (status === "failed") {
    return generation?.error || "Generation failed.";
  }
  return null;
}
